import React, { useState } from 'react';
import { X, MessageSquare, Mail, Send, Monitor } from 'lucide-react';

interface SendLiabilityWaiverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendSMS: () => void;
  onSendEmail: () => void;
  onSignOnDevice: () => void;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  isLoading: boolean;
}

export default function SendLiabilityWaiverModal({
  isOpen,
  onClose,
  onSendSMS,
  onSendEmail,
  onSignOnDevice,
  customerName,
  customerPhone,
  customerEmail,
  isLoading
}: SendLiabilityWaiverModalProps) {
  if (!isOpen) return null;

  const hasPhone = Boolean(customerPhone);
  const hasEmail = Boolean(customerEmail);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Send className="h-5 w-5 mr-2" />
            Send Liability Waiver
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            How would you like to send the liability waiver to <span className="font-medium">{customerName}</span>?
          </p>

          <div className="space-y-3">
            {/* SMS Option */}
            <button
              onClick={onSendSMS}
              disabled={!hasPhone || isLoading}
              className={`w-full flex items-center justify-between p-3 border rounded-lg transition-colors ${
                hasPhone && !isLoading
                  ? 'border-gray-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                <MessageSquare className={`h-5 w-5 mr-3 ${hasPhone ? 'text-blue-600' : 'text-gray-400'}`} />
                <div className="text-left">
                  <div className={`font-medium ${hasPhone ? 'text-gray-900' : 'text-gray-500'}`}>
                    Send via SMS
                  </div>
                  <div className="text-xs text-gray-500">
                    {hasPhone ? customerPhone : 'No phone number available'}
                  </div>
                </div>
              </div>
              {hasPhone && (
                <div className="text-xs text-blue-600 font-medium">Recommended</div>
              )}
            </button>

            {/* Email Option */}
            <button
              onClick={onSendEmail}
              disabled={!hasEmail || isLoading}
              className={`w-full flex items-center justify-between p-3 border rounded-lg transition-colors ${
                hasEmail && !isLoading
                  ? 'border-gray-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                <Mail className={`h-5 w-5 mr-3 ${hasEmail ? 'text-green-600' : 'text-gray-400'}`} />
                <div className="text-left">
                  <div className={`font-medium ${hasEmail ? 'text-gray-900' : 'text-gray-500'}`}>
                    Send via Email
                  </div>
                  <div className="text-xs text-gray-500">
                    {hasEmail ? customerEmail : 'No email address available'}
                  </div>
                </div>
              </div>
            </button>

            {/* Sign on Device Option */}
            <button
              onClick={onSignOnDevice}
              disabled={isLoading}
              className={`w-full flex items-center justify-between p-3 border rounded-lg transition-colors ${
                !isLoading
                  ? 'border-gray-300 hover:border-orange-500 hover:bg-orange-50 cursor-pointer'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center">
                <Monitor className="h-5 w-5 mr-3 text-orange-600" />
                <div className="text-left">
                  <div className="font-medium text-gray-900">
                    Sign on this device
                  </div>
                  <div className="text-xs text-gray-500">
                    Open waiver directly on this device
                  </div>
                </div>
              </div>
              <div className="text-xs text-orange-600 font-medium">Fallback option</div>
            </button>
          </div>

          {/* Info if no contact methods available */}
          {!hasPhone && !hasEmail && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-800">
                💡 No contact information available. You can use "Sign on this device" to have the customer sign directly.
              </div>
            </div>
          )}

          {/* Info about the waiver */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600">
              The customer will receive a secure link to view and sign the liability waiver. 
              Once signed, the status will be updated automatically.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
} 